import { Button, DatePicker, Form, Input, Modal, Popconfirm, Row, Space, Table, Typography } from "antd";
import { FormInstance } from "antd/lib/form";
import moment from "moment";
import React from "react";

// Types
interface ConsumptionRecord {
  key: string;
  locationId: string;
  consumptionPeriodStart: string;
  consumptionPeriodEnd: string;
  metricTypeId: number;
  metricName: string;
  actualValue: number | null;
  clientForecast: number | null;
  sensorflowForecast: number | null;
  sourceType: string;
  sourceId: string;
  updatedAt: string;
  isEditing?: boolean;
  actualCost?: number | null;
  clientCostForecast?: number | null;
}

interface BulkEditConsumptionDataModalProps {
  visible: boolean;
  onClose: () => void;
  editConsumptionData: ConsumptionRecord[];
  editConsumptionForm: FormInstance;
  consumptionYear: moment.Moment;
  onYearChange: (date: moment.Moment) => void;
  generateMonthlyConsumptionData: (yearOverride?: moment.Moment) => ConsumptionRecord[];
  setEditConsumptionData: (data: ConsumptionRecord[]) => void;
  getMetricUnit: (metricName: string) => string;
  formatMetricTypeName: (metricName: string) => string;
  tableRef: React.RefObject<HTMLDivElement>;
  handleInputFocus: (fieldType: string, recordKey: string) => void;
  handleInputBlur: () => void;
  handleInputPaste: (e: React.ClipboardEvent, recordKey: string, fieldType: string) => void;
  handleClearAllConsumption: () => void;
  isSavingConsumption: boolean;
  handleEditConsumptionSubmit: () => Promise<void>;
}

const BulkEditConsumptionDataModal: React.FC<BulkEditConsumptionDataModalProps> = ({
  visible,
  onClose,
  editConsumptionData,
  editConsumptionForm,
  consumptionYear,
  onYearChange,
  generateMonthlyConsumptionData,
  setEditConsumptionData,
  getMetricUnit,
  formatMetricTypeName,
  tableRef,
  handleInputFocus,
  handleInputBlur,
  handleInputPaste,
  handleClearAllConsumption,
  isSavingConsumption,
  handleEditConsumptionSubmit,
}) => {
  const handleYearChange = (date: moment.Moment | null) => {
    if (date) {
      onYearChange(date);
      // Regenerate data for the new year - pass the new date directly
      const newMonthlyData = generateMonthlyConsumptionData(date);
      setEditConsumptionData(newMonthlyData);

      // Reset form with new data
      editConsumptionForm.resetFields();
      const initialValues: { [key: string]: string } = {};
      newMonthlyData.forEach((record) => {
        initialValues[`actualValue-${record.key}`] = record.actualValue !== null ? record.actualValue.toString() : "";
        initialValues[`actualCost-${record.key}`] =
          record.actualCost !== null && record.actualCost !== undefined ? record.actualCost.toString() : "";
        initialValues[`clientForecast-${record.key}`] =
          record.clientForecast !== null ? record.clientForecast.toString() : "";
        initialValues[`clientCostForecast-${record.key}`] =
          record.clientCostForecast !== null && record.clientCostForecast !== undefined
            ? record.clientCostForecast.toString()
            : "";
      });
      editConsumptionForm.setFieldsValue(initialValues);
    }
  };

  const handleCancel = () => {
    onClose();
    editConsumptionForm.resetFields();
  };

  return (
    <Modal
      title={`Bulk Edit Consumption Data${
        editConsumptionData.length > 0 ? ` - ${formatMetricTypeName(editConsumptionData[0].metricName)}` : ""
      }`}
      visible={visible}
      onCancel={handleCancel}
      footer={null}
      width={1200}
      destroyOnClose
    >
      <div className="edit-consumption-container">
        <Form form={editConsumptionForm} layout="vertical" className="consumption-form">
          <div className="year-selector" style={{ marginBottom: 16 }}>
            <Space>
              <Typography.Text strong>Year:</Typography.Text>
              <DatePicker
                picker="year"
                value={consumptionYear as any}
                onChange={handleYearChange}
                style={{ width: 120 }}
              />
            </Space>
          </div>

          <div className="table-info-message" style={{ marginBottom: 16 }}>
            <Typography.Text type="secondary">
              You can copy-paste values from Excel directly into the table. Multi-line paste will fill consecutive rows.
              For best results, select a cell and use Ctrl+V (Cmd+V on Mac) to paste data.
            </Typography.Text>
          </div>

          <div ref={tableRef} className="paste-capture-container" style={{ outline: "none" }}>
            <Table
              columns={[
                {
                  title: "Billing Period From",
                  dataIndex: "periodFrom",
                  key: "periodFrom",
                  width: "15%",
                  render: (_: any, record: ConsumptionRecord) => (
                    <div>
                      <div>{moment(record.consumptionPeriodStart).format("MMM D, YYYY")}</div>
                    </div>
                  ),
                },
                {
                  title: "Billing Period To",
                  dataIndex: "periodTo",
                  key: "periodTo",
                  width: "15%",
                  render: (_: any, record: ConsumptionRecord) => (
                    <div>
                      <div>{moment(record.consumptionPeriodEnd).format("MMM D, YYYY")}</div>
                    </div>
                  ),
                },
                {
                  title: `Actual Value (${getMetricUnit(editConsumptionData[0]?.metricName || "")})`,
                  dataIndex: "actualValue",
                  key: "actualValue",
                  width: "17.5%",
                  render: (_: any, record: ConsumptionRecord) => (
                    <Form.Item
                      name={`actualValue-${record.key}`}
                      initialValue={record.actualValue !== null ? record.actualValue.toString() : ""}
                      style={{ margin: 0 }}
                      rules={[
                        {
                          validator: (_rule, value) => {
                            if (!value) return Promise.resolve();
                            const num = parseFloat(value);
                            if (Number.isNaN(num)) return Promise.reject(new Error("Must be a number"));
                            if (num < 0) return Promise.reject(new Error("Must be positive"));
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        id={`actualValue-${record.key}`}
                        type="text"
                        placeholder="Enter value"
                        suffix={getMetricUnit(record.metricName)}
                        onFocus={() => handleInputFocus("actualValue", record.key)}
                        onBlur={handleInputBlur}
                        onPaste={(e) => handleInputPaste(e, record.key, "actualValue")}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: "Actual Cost ($)",
                  dataIndex: "actualCost",
                  key: "actualCost",
                  width: "17.5%",
                  render: (_: any, record: ConsumptionRecord) => (
                    <Form.Item
                      name={`actualCost-${record.key}`}
                      initialValue={
                        record.actualCost !== null && record.actualCost !== undefined
                          ? record.actualCost.toString()
                          : ""
                      }
                      style={{ margin: 0 }}
                      rules={[
                        {
                          validator: (_rule, value) => {
                            if (!value) return Promise.resolve();
                            const num = parseFloat(value);
                            if (Number.isNaN(num)) return Promise.reject(new Error("Must be a number"));
                            if (num < 0) return Promise.reject(new Error("Must be positive"));
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        id={`actualCost-${record.key}`}
                        type="text"
                        placeholder="Enter cost"
                        prefix="$"
                        onFocus={() => handleInputFocus("actualCost", record.key)}
                        onBlur={handleInputBlur}
                        onPaste={(e) => handleInputPaste(e, record.key, "actualCost")}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: `Forecast Value (${getMetricUnit(editConsumptionData[0]?.metricName || "")})`,
                  dataIndex: "clientForecast",
                  key: "clientForecast",
                  width: "17.5%",
                  render: (_: any, record: ConsumptionRecord) => (
                    <Form.Item
                      name={`clientForecast-${record.key}`}
                      initialValue={record.clientForecast !== null ? record.clientForecast.toString() : ""}
                      style={{ margin: 0 }}
                      rules={[
                        {
                          validator: (_rule, value) => {
                            if (!value) return Promise.resolve();
                            const num = parseFloat(value);
                            if (Number.isNaN(num)) return Promise.reject(new Error("Must be a number"));
                            if (num < 0) return Promise.reject(new Error("Must be positive"));
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        id={`clientForecast-${record.key}`}
                        type="text"
                        placeholder="Enter forecast"
                        suffix={getMetricUnit(record.metricName)}
                        onFocus={() => handleInputFocus("clientForecast", record.key)}
                        onBlur={handleInputBlur}
                        onPaste={(e) => handleInputPaste(e, record.key, "clientForecast")}
                      />
                    </Form.Item>
                  ),
                },
                {
                  title: "Forecast Cost ($)",
                  dataIndex: "clientCostForecast",
                  key: "clientCostForecast",
                  width: "17.5%",
                  render: (_: any, record: ConsumptionRecord) => (
                    <Form.Item
                      name={`clientCostForecast-${record.key}`}
                      initialValue={
                        record.clientCostForecast !== null && record.clientCostForecast !== undefined
                          ? record.clientCostForecast.toString()
                          : ""
                      }
                      style={{ margin: 0 }}
                      rules={[
                        {
                          validator: (_rule, value) => {
                            if (!value) return Promise.resolve();
                            const num = parseFloat(value);
                            if (Number.isNaN(num)) return Promise.reject(new Error("Must be a number"));
                            if (num < 0) return Promise.reject(new Error("Must be positive"));
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input
                        id={`clientCostForecast-${record.key}`}
                        type="text"
                        placeholder="Enter forecast cost"
                        prefix="$"
                        onFocus={() => handleInputFocus("clientCostForecast", record.key)}
                        onBlur={handleInputBlur}
                        onPaste={(e) => handleInputPaste(e, record.key, "clientCostForecast")}
                      />
                    </Form.Item>
                  ),
                },
              ]}
              dataSource={editConsumptionData}
              rowKey="key"
              pagination={false}
              bordered
              size="middle"
              scroll={{ y: 500 }}
            />
          </div>

          <Row justify="end" style={{ marginTop: 16 }}>
            <Space>
              <Button onClick={handleCancel}>Cancel</Button>
              <Popconfirm
                title="Are you sure you want to clear all values?"
                onConfirm={handleClearAllConsumption}
                okText="Yes"
                cancelText="No"
              >
                <Button>Clear All</Button>
              </Popconfirm>
              <Button type="primary" loading={isSavingConsumption} onClick={handleEditConsumptionSubmit}>
                {isSavingConsumption ? "Saving..." : "Save"}
              </Button>
            </Space>
          </Row>
        </Form>
      </div>
    </Modal>
  );
};

export default BulkEditConsumptionDataModal;
